import React, { useMemo, useState } from "react";
import Search from "../../../components/common/Search";
import CurrencyFlag from "../components/CurrencyFlag";
import { ReactComponent as CheckIcon } from "../../../assets/icons/check-icon.svg";
import {
  AllWalletsEmpty,
  AllWalletsItem,
  AllWalletsList,
  AllWalletsModalWrapper,
  AllWalletsSectionLabel,
  AllWalletsTitle,
  WalletCardHeader,
  WalletCardLabel,
} from "./style";
import {
  formatCompactWalletBalance,
  getRecentWallets,
  getWalletName,
  getWalletRegion,
} from "../utils";
import { WalletSummary } from "../../../types/walletTypes";

type AllWalletsModalProps = {
  isOpen: boolean;
  wallets: WalletSummary[];
  onClose: () => void;
  onSelectWallet: (wallet: WalletSummary) => void;
};

const AllWalletsModal = ({ isOpen, wallets, onClose, onSelectWallet }: AllWalletsModalProps) => {
  const [search, setSearch] = useState("");

  const filteredWallets = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return wallets;
    return wallets.filter(
      ({ currency }) =>
        currency.toLowerCase().includes(query) ||
        getWalletName(currency).toLowerCase().includes(query) ||
        getWalletRegion(currency).toLowerCase().includes(query)
    );
  }, [search, wallets]);

  const recentCurrencies = getRecentWallets();
  const recentWallets = recentCurrencies
    .map((currency) => filteredWallets.find((wallet) => wallet.currency === currency))
    .filter(Boolean);
  const otherWallets = filteredWallets.filter(
    (wallet) => !recentCurrencies.includes(wallet.currency)
  );

  const renderWallet = (wallet: WalletSummary, isLastViewed = false) => {
    const region = getWalletRegion(wallet.currency);
    return (
      <AllWalletsItem key={wallet.currency} type="button" onClick={() => onSelectWallet(wallet)}>
        <WalletCardHeader>
          <CurrencyFlag currency={wallet.currency} length={32} />
          <WalletCardLabel>
            <strong>{wallet.currency}</strong>
            <span>
              {getWalletName(wallet.currency)}
              {region && ` · ${region}`}
            </span>
          </WalletCardLabel>
        </WalletCardHeader>
        {isLastViewed ? (
          <CheckIcon aria-label="Last viewed wallet" />
        ) : (
          <span className="balance">
            Balance {formatCompactWalletBalance(wallet.currency, wallet.availableBalance)}
          </span>
        )}
      </AllWalletsItem>
    );
  };

  return (
    <AllWalletsModalWrapper isModalVisible={isOpen} onClose={onClose} width="464px">
      <AllWalletsTitle>All Wallets</AllWalletsTitle>
      <Search
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search currency"
        width="100%"
        height="40px"
        fontSize="12px"
        showClose={!!search}
        clearSearch={() => setSearch("")}
      />

      <AllWalletsList>
        {filteredWallets.length === 0 && <AllWalletsEmpty>No wallet found</AllWalletsEmpty>}

        {recentWallets.length > 0 && (
          <>
            <AllWalletsSectionLabel>Recent</AllWalletsSectionLabel>
            {recentWallets.map((wallet, index) => renderWallet(wallet, index === 0))}
          </>
        )}

        {otherWallets.length > 0 && (
          <>
            <AllWalletsSectionLabel>Popular</AllWalletsSectionLabel>
            {otherWallets.map((wallet) => renderWallet(wallet))}
          </>
        )}
      </AllWalletsList>
    </AllWalletsModalWrapper>
  );
};

export default AllWalletsModal;
